/**
 * Translation strings for NL/EN localization
 */

export type Language = 'nl' | 'en';

export const translations: Record<Language, Record<string, string>> = {
  nl: {
    // Header navigation
    'nav.orders': 'Bestellingen',
    'nav.cart': 'Winkelwagen',
    'nav.admin': 'Admin',
    'nav.memory': 'Geheugen',

    // Header buttons
    'header.login': 'Inloggen',
    'header.logout': 'Uitloggen',
    'header.search': 'Zoeken...',
    'header.reset_demo': 'Reset Demo',
    'header.tutorial': 'Tutorial',
    'header.theme': 'Thema',
    'header.act_as': 'Act As',
    'header.stop_acting': 'Stop',

    // Voice overlay
    'voice.call_sophie': 'Bel Sophie',
    'voice.connecting': 'Verbinden...',
    'voice.connected': 'Verbonden',
    'voice.disconnected': 'Verbinding verbroken',
    'voice.error': 'Fout bij verbinden',
    'voice.hold_to_talk': 'Druk SPATIE om te praten',
    'voice.listening': 'Luisteren...',
    'voice.speaking': 'Sophie spreekt...',
    'voice.you_speaking': 'Je spreekt...',
    'voice.end_call': 'Ophangen',
    'voice.click_to_start': 'Klik om te starten',
    'voice.noisy_environment': 'Luidruchtige omgeving?',
    'voice.noisy_hint': 'Gebruik push-to-talk om achtergrondgeluid te vermijden.',
    'voice.use_ptt': 'Push-to-talk gebruiken',
    'voice.open_mic': 'Open microfoon',
    'voice.type_message': 'Type je bericht...',
    'voice.click_mic_to_start': 'Klik op de microfoon om te starten',
    'voice.switch_to_voice': 'Schakel naar spraak',
    'voice.end_chat': 'Einde chat',
    'voice.show_chat': 'Toon chat',
    'voice.stop_call': 'Stop gesprek',
    'voice.ptt_on': 'Push-to-talk AAN',
    'voice.ptt_off': 'Push-to-talk UIT',
    'voice.hold_space': 'Houd SPATIE ingedrukt om te praten!',

    // Store page
    'store.welcome': 'Welkom bij de',
    'store.voice_demo': 'Voice Demo',
    'store.categories_title': 'Categorieën',
    'store.subcategories_in': 'Subcategorieën in {category}',
    'store.all_in_category': 'Alle',
    'store.products_title': 'Producten',
    'store.hero_subtitle': 'Ervaar de toekomst van klantenservice! Onze AI-assistent begrijpt je in het Nederlands, helpt je met producten zoeken, bestellingen tracken en meer — allemaal via een natuurlijk gesprek.',
    'store.feature_voice': 'Real-time voice chat',
    'store.feature_products': '500+ producten doorzoekbaar',
    'store.feature_instant': 'Instant antwoorden',
    'store.categories': 'Categorieën',
    'store.subcategories': 'Subcategorieën',
    'store.all_products': 'Alle producten',
    'store.no_products': 'Geen producten gevonden',
    'store.loading': 'Laden...',
    'store.sort': 'Sorteer',
    'store.sort.relevance': 'Relevantie',
    'store.sort.price_asc': 'Prijs laag-hoog',
    'store.sort.price_desc': 'Prijs hoog-laag',
    'store.sort.name': 'Naam A-Z',
    'store.filters': 'Filters',
    'store.clear_filters': 'Filters wissen',
    'store.results': '{count} resultaten',
    'store.out_of_stock': 'Uitverkocht',
    'store.in_stock': 'Op voorraad',

    // Cart
    'cart.title': 'Winkelwagen',
    'cart.empty': 'Je winkelwagen is leeg',
    'cart.empty_desc': 'Ontdek onze producten en voeg iets toe aan je winkelwagen.',
    'cart.continue_shopping': 'Verder winkelen',
    'cart.checkout': 'Afrekenen',
    'cart.total': 'Totaal',
    'cart.subtotal': 'Subtotaal',
    'cart.remove': 'Verwijderen',
    'cart.quantity': 'Aantal',

    // Orders
    'orders.title': 'Mijn Bestellingen',
    'orders.no_orders': 'Je hebt nog geen bestellingen',
    'orders.order_number': 'Bestelnummer',
    'orders.order_date': 'Besteldatum',
    'orders.status': 'Status',
    'orders.track': 'Track & Trace',
    'orders.details': 'Details',
    'orders.items': '{count} artikel(en)',

    // Order statuses
    'status.pending': 'In behandeling',
    'status.processing': 'In behandeling',
    'status.shipped': 'Onderweg',
    'status.delivered': 'Bezorgd',
    'status.cancelled': 'Geannuleerd',
    'status.returned': 'Geretourneerd',
    'status.refunded': 'Terugbetaald',
    'status.return_requested': 'Retour gepland',
    'status.pickup_scheduled': 'Ophalen gepland',
    'status.unknown': 'Onbekend',

    // Order history page
    'orders.delivery': 'Bezorging',
    'orders.item': 'artikel',
    'orders.items_plural': 'artikelen',
    'orders.no_orders_desc': 'Je hebt nog geen bestellingen geplaatst. Begin met winkelen!',
    'orders.go_to_store': 'Naar de winkel',

    // Login
    'login.title': 'Inloggen',
    'login.email': 'E-mailadres',
    'login.password': 'Wachtwoord',
    'login.password_placeholder': 'Je wachtwoord',
    'login.submit': 'Inloggen',
    'login.forgot_password': 'Wachtwoord vergeten?',
    'login.google': 'Inloggen met Google',
    'login.register_info': 'Geen account? Neem contact op met de beheerder.',
    'login.no_account': 'Geen account?',
    'login.request_access': 'Toegang aanvragen',
    'login.back_home': 'Terug naar home',
    'login.error': 'Inloggen mislukt',
    'login.reset_title': 'Wachtwoord resetten',
    'login.reset_submit': 'Verstuur reset link',
    'login.reset_sent': 'Reset link verzonden naar {email}. Check je inbox.',
    'login.back_to_login': 'Terug naar inloggen',
    'login.loading': 'Bezig...',
    'login.or': 'of',
    'login.demo_subtitle': 'Klantenservice Demo',

    // Categories (specific names come from API)
    'category.all': 'Alles',

    // General
    'general.loading': 'Laden...',
    'general.error': 'Er ging iets mis',
    'general.retry': 'Opnieuw proberen',
    'general.cancel': 'Annuleren',
    'general.confirm': 'Bevestigen',
    'general.save': 'Opslaan',
    'general.close': 'Sluiten',
    'general.back': 'Terug',
    'general.next': 'Volgende',
    'general.yes': 'Ja',
    'general.no': 'Nee',

    // Pagination
    'pagination.page_of': 'Pagina {page} van {total_pages} ({total_count} totaal)',
    'pagination.previous': 'Vorige',
    'pagination.next': 'Volgende',
    'pagination.of_products': '{start}-{end} van {total} producten',
    'pagination.highlighted': 'gehighlight',

    // Sort buttons
    'store.sort_label': 'Sorteer:',
    'store.sort_best': '⭐ Beste',
    'store.sort_price_low': '€ Laag-Hoog',
    'store.sort_price_high': '€ Hoog-Laag',

    // Admin page
    'admin.title': 'Admin Dashboard',
    'admin.my_overview': 'Mijn Overzicht',
    'admin.users': 'Gebruikers',
    'admin.orders': 'Bestellingen',
    'admin.returns': 'Retouren',
    'admin.pickups': 'Ophaal afspraken',
    'admin.callbacks': 'Terugbelverzoeken',
    'admin.phone_calls': 'Telefoongesprekken',
    'admin.text_chats': 'Text',
    'admin.voice_chats': 'Voice',
    'admin.phone': 'Tel',
    'admin.my_profile': 'Mijn Profiel',
    'admin.manage_users': 'Gebruikers beheren',
    'admin.invite': 'Uitnodigen',
    'admin.refresh': 'Vernieuwen',
    'admin.settings': 'Instellingen',
    'admin.access_requests': 'Toegangsverzoeken',
    'admin.access_approve': 'Goedkeuren',
    'admin.access_dismiss': 'Afwijzen',

    // Phone number modal
    'phone_modal.title': 'Telefoonnummer toevoegen',
    'phone_modal.subtitle': 'Voor een betere ervaring',
    'phone_modal.description': 'Voeg je telefoonnummer toe zodat onze telefoonassistent je direct herkent als je belt. Zo hoef je jezelf niet elke keer opnieuw te identificeren.',
    'phone_modal.label': 'Telefoonnummer',
    'phone_modal.consent': 'Door je nummer op te slaan, sta je ons toe je te herkennen bij telefonisch contact en je een persoonlijke ervaring te bieden.',
    'phone_modal.skip': 'Later',

    // Access request page
    'access_request.title': 'Toegang aanvragen',
    'access_request.subtitle': 'Vul het formulier in en we nemen zo snel mogelijk contact met je op.',
    'access_request.name': 'Naam',
    'access_request.email': 'E-mailadres',
    'access_request.company': 'Bedrijf',
    'access_request.motivation': 'Motivatie',
    'access_request.motivation_placeholder': 'Waarom wil je toegang? Vertel ons over je use case...',
    'access_request.submit': 'Aanvraag versturen',
    'access_request.submitting': 'Versturen...',
    'access_request.success_title': 'Aanvraag verzonden!',
    'access_request.success_message': 'Bedankt voor je interesse! We bekijken je aanvraag en nemen zo snel mogelijk contact met je op.',
    'access_request.back_home': 'Terug naar home',
    'access_request.already_have_account': 'Al een account? Inloggen',
    'access_request.error': 'Er ging iets mis. Probeer het later opnieuw.',
  },

  en: {
    // Header navigation
    'nav.orders': 'Orders',
    'nav.cart': 'Cart',
    'nav.admin': 'Admin',
    'nav.memory': 'Memory',

    // Header buttons
    'header.login': 'Sign in',
    'header.logout': 'Sign out',
    'header.search': 'Search...',
    'header.reset_demo': 'Reset Demo',
    'header.tutorial': 'Tutorial',
    'header.theme': 'Theme',
    'header.act_as': 'Act As',
    'header.stop_acting': 'Stop',

    // Voice overlay
    'voice.call_sophie': 'Call Sophie',
    'voice.connecting': 'Connecting...',
    'voice.connected': 'Connected',
    'voice.disconnected': 'Disconnected',
    'voice.error': 'Connection error',
    'voice.hold_to_talk': 'Press SPACE to talk',
    'voice.listening': 'Listening...',
    'voice.speaking': 'Sophie is speaking...',
    'voice.you_speaking': 'You are speaking...',
    'voice.end_call': 'End call',
    'voice.click_to_start': 'Click to start',
    'voice.noisy_environment': 'Noisy environment?',
    'voice.noisy_hint': 'Use push-to-talk to avoid background noise.',
    'voice.use_ptt': 'Use push-to-talk',
    'voice.open_mic': 'Open microphone',
    'voice.type_message': 'Type your message...',
    'voice.click_mic_to_start': 'Click the microphone to start',
    'voice.switch_to_voice': 'Switch to voice',
    'voice.end_chat': 'End chat',
    'voice.show_chat': 'Show chat',
    'voice.stop_call': 'Stop call',
    'voice.ptt_on': 'Push-to-talk ON',
    'voice.ptt_off': 'Push-to-talk OFF',
    'voice.hold_space': 'Hold SPACE to talk!',

    // Store page
    'store.welcome': 'Welcome to the',
    'store.voice_demo': 'Voice Demo',
    'store.categories_title': 'Categories',
    'store.subcategories_in': 'Subcategories in {category}',
    'store.all_in_category': 'All',
    'store.products_title': 'Products',
    'store.hero_subtitle': 'Experience the future of customer service! Our AI assistant understands you in English, helps you find products, track orders and more — all through a natural conversation.',
    'store.feature_voice': 'Real-time voice chat',
    'store.feature_products': '500+ searchable products',
    'store.feature_instant': 'Instant answers',
    'store.categories': 'Categories',
    'store.subcategories': 'Subcategories',
    'store.all_products': 'All products',
    'store.no_products': 'No products found',
    'store.loading': 'Loading...',
    'store.sort': 'Sort',
    'store.sort.relevance': 'Relevance',
    'store.sort.price_asc': 'Price low-high',
    'store.sort.price_desc': 'Price high-low',
    'store.sort.name': 'Name A-Z',
    'store.filters': 'Filters',
    'store.clear_filters': 'Clear filters',
    'store.results': '{count} results',
    'store.out_of_stock': 'Out of stock',
    'store.in_stock': 'In stock',

    // Cart
    'cart.title': 'Shopping Cart',
    'cart.empty': 'Your cart is empty',
    'cart.empty_desc': 'Discover our products and add something to your cart.',
    'cart.continue_shopping': 'Continue shopping',
    'cart.checkout': 'Checkout',
    'cart.total': 'Total',
    'cart.subtotal': 'Subtotal',
    'cart.remove': 'Remove',
    'cart.quantity': 'Quantity',

    // Orders
    'orders.title': 'My Orders',
    'orders.no_orders': "You don't have any orders yet",
    'orders.order_number': 'Order number',
    'orders.order_date': 'Order date',
    'orders.status': 'Status',
    'orders.track': 'Track & Trace',
    'orders.details': 'Details',
    'orders.items': '{count} item(s)',

    // Order statuses
    'status.pending': 'Pending',
    'status.processing': 'Processing',
    'status.shipped': 'Shipped',
    'status.delivered': 'Delivered',
    'status.cancelled': 'Cancelled',
    'status.returned': 'Returned',
    'status.refunded': 'Refunded',
    'status.return_requested': 'Return requested',
    'status.pickup_scheduled': 'Pickup scheduled',
    'status.unknown': 'Unknown',

    // Order history page
    'orders.delivery': 'Delivery',
    'orders.item': 'item',
    'orders.items_plural': 'items',
    'orders.no_orders_desc': "You haven't placed any orders yet. Start shopping!",
    'orders.go_to_store': 'Go to store',

    // Login
    'login.title': 'Sign in',
    'login.email': 'Email address',
    'login.password': 'Password',
    'login.password_placeholder': 'Your password',
    'login.submit': 'Sign in',
    'login.forgot_password': 'Forgot password?',
    'login.google': 'Sign in with Google',
    'login.register_info': "Don't have an account? Contact the administrator.",
    'login.no_account': "Don't have an account?",
    'login.request_access': 'Request Access',
    'login.back_home': 'Back to home',
    'login.error': 'Sign in failed',
    'login.reset_title': 'Reset password',
    'login.reset_submit': 'Send reset link',
    'login.reset_sent': 'Reset link sent to {email}. Check your inbox.',
    'login.back_to_login': 'Back to sign in',
    'login.loading': 'Loading...',
    'login.or': 'or',
    'login.demo_subtitle': 'Customer Service Demo',

    // Categories (specific names come from API)
    'category.all': 'All',

    // General
    'general.loading': 'Loading...',
    'general.error': 'Something went wrong',
    'general.retry': 'Try again',
    'general.cancel': 'Cancel',
    'general.confirm': 'Confirm',
    'general.save': 'Save',
    'general.close': 'Close',
    'general.back': 'Back',
    'general.next': 'Next',
    'general.yes': 'Yes',
    'general.no': 'No',

    // Pagination
    'pagination.page_of': 'Page {page} of {total_pages} ({total_count} total)',
    'pagination.previous': 'Previous',
    'pagination.next': 'Next',
    'pagination.of_products': '{start}-{end} of {total} products',
    'pagination.highlighted': 'highlighted',

    // Sort buttons
    'store.sort_label': 'Sort:',
    'store.sort_best': '⭐ Best',
    'store.sort_price_low': '€ Low-High',
    'store.sort_price_high': '€ High-Low',

    // Admin page
    'admin.title': 'Admin Dashboard',
    'admin.my_overview': 'My Overview',
    'admin.users': 'Users',
    'admin.orders': 'Orders',
    'admin.returns': 'Returns',
    'admin.pickups': 'Pickup appointments',
    'admin.callbacks': 'Callback requests',
    'admin.phone_calls': 'Phone calls',
    'admin.text_chats': 'Text',
    'admin.voice_chats': 'Voice',
    'admin.phone': 'Phone',
    'admin.my_profile': 'My Profile',
    'admin.manage_users': 'Manage users',
    'admin.invite': 'Invite',
    'admin.refresh': 'Refresh',
    'admin.settings': 'Settings',
    'admin.access_requests': 'Access Requests',
    'admin.access_approve': 'Approve',
    'admin.access_dismiss': 'Dismiss',

    // Phone number modal
    'phone_modal.title': 'Add phone number',
    'phone_modal.subtitle': 'For a better experience',
    'phone_modal.description': 'Add your phone number so our phone assistant recognizes you when you call. This way you won\'t have to identify yourself every time.',
    'phone_modal.label': 'Phone number',
    'phone_modal.consent': 'By saving your number, you allow us to recognize you during phone calls and provide a personalized experience.',
    'phone_modal.skip': 'Later',

    // Access request page
    'access_request.title': 'Request Access',
    'access_request.subtitle': 'Fill in the form and we will get back to you as soon as possible.',
    'access_request.name': 'Name',
    'access_request.email': 'Email address',
    'access_request.company': 'Company',
    'access_request.motivation': 'Motivation',
    'access_request.motivation_placeholder': 'Why do you want access? Tell us about your use case...',
    'access_request.submit': 'Submit Request',
    'access_request.submitting': 'Submitting...',
    'access_request.success_title': 'Request Submitted!',
    'access_request.success_message': 'Thanks for your interest! We will review your request and get back to you as soon as possible.',
    'access_request.back_home': 'Back to home',
    'access_request.already_have_account': 'Already have an account? Sign in',
    'access_request.error': 'Something went wrong. Please try again later.',
  },
};

/**
 * Get a translation with optional interpolation
 */
export function translate(language: Language, key: string, params?: Record<string, string | number>): string {
  let text = translations[language]?.[key] || translations['nl'][key] || key;

  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, String(v));
    });
  }

  return text;
}
